import * as React from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import * as DialogPrimitive from '@radix-ui/react-dialog'
import { X } from 'lucide-react'
import { cva, type VariantProps } from 'class-variance-authority'
import { cn } from '@/utils/cn'

const Dialog = DialogPrimitive.Root
const DialogTrigger = DialogPrimitive.Trigger
const DialogPortal = DialogPrimitive.Portal
const DialogClose = DialogPrimitive.Close

const dialogContentVariants = cva(
  [
    'fixed left-1/2 top-1/2 z-50 flex w-[calc(100%-0.75rem)] -translate-x-1/2 -translate-y-1/2 flex-col',
    'max-h-[min(90dvh,calc(100dvh-env(safe-area-inset-top)-env(safe-area-inset-bottom)-1.5rem))] overflow-hidden',
    // Glass system (Level 3 overlay)
    'glass-overlay rounded-xl outline-none',
    'sm:w-[calc(100%-2rem)]',
  ],
  {
    variants: {
      size: {
        sm: 'max-w-[400px]',
        md: 'max-w-lg',
        lg: 'max-w-2xl',
        xl: 'max-w-4xl',
        '2xl': 'max-w-5xl',
      },
    },
    defaultVariants: {
      size: 'md',
    },
  },
)

const DialogOverlay = React.forwardRef<
  React.ComponentRef<typeof DialogPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Overlay ref={ref} asChild {...props}>
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.12 }}
      className={cn(
        'fixed inset-0 z-50 bg-slate-900/50',
        className,
      )}
    />
  </DialogPrimitive.Overlay>
))
DialogOverlay.displayName = DialogPrimitive.Overlay.displayName

interface DialogContentProps
  extends React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content>,
    VariantProps<typeof dialogContentVariants> {
  showClose?: boolean
  compact?: boolean
}

const DialogContent = React.forwardRef<
  React.ComponentRef<typeof DialogPrimitive.Content>,
  DialogContentProps
>(({ className, children, size, showClose = true, compact = false, ...props }, ref) => (
  <DialogPortal>
    <DialogOverlay />
    <DialogPrimitive.Content ref={ref} asChild {...props}>
      <motion.div
        initial={{ opacity: 0, scale: 0.98 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.98 }}
        transition={{ duration: 0.14, ease: [0.16, 1, 0.3, 1] }}
        className={cn(dialogContentVariants({ size }), className)}
      >
        {children}
        {showClose ? (
          <DialogPrimitive.Close
            className={cn(
              'absolute z-20 rounded-md text-muted-foreground transition-colors hover:bg-secondary hover:text-foreground focus:outline-none focus:ring-2 focus:ring-ring',
              compact
                ? 'right-2 top-2 p-1'
                : 'right-2.5 top-2.5 p-1.5 sm:right-3 sm:top-3',
            )}
          >
            <X className={compact ? 'h-3.5 w-3.5' : 'h-4 w-4'} />
            <span className="sr-only">Close</span>
          </DialogPrimitive.Close>
        ) : null}
      </motion.div>
    </DialogPrimitive.Content>
  </DialogPortal>
))
DialogContent.displayName = DialogPrimitive.Content.displayName

function DialogHeader({
  className,
  compact = false,
  ...props
}: React.ComponentProps<'div'> & { compact?: boolean }) {
  return (
    <div
      className={cn(
        'sticky top-0 z-10 shrink-0 border-b border-border/80 bg-card/95 backdrop-blur-sm',
        compact ? 'px-3 py-2 pr-9' : 'px-3 py-2.5 pr-10 sm:px-4 sm:pr-11',
        className,
      )}
      {...props}
    />
  )
}

function DialogBody({
  className,
  compact = false,
  ...props
}: React.ComponentProps<'div'> & { compact?: boolean }) {
  return (
    <div
      className={cn(
        'flex-1 overflow-y-auto scrollbar-thin',
        compact ? 'px-3 py-2' : 'px-3 py-2.5 sm:px-4 sm:py-3',
        className,
      )}
      {...props}
    />
  )
}

function DialogFooter({
  className,
  compact = false,
  ...props
}: React.ComponentProps<'div'> & { compact?: boolean }) {
  return (
    <div
      className={cn(
        'sticky bottom-0 z-10 flex shrink-0 flex-col-reverse gap-1.5 border-t border-border/80 bg-card/95 pb-[max(0.5rem,env(safe-area-inset-bottom))] backdrop-blur-sm sm:flex-row sm:justify-end',
        compact
          ? 'px-3 py-1.5 [&_button]:h-7 [&_button]:rounded-md [&_button]:px-2.5 [&_button]:text-xs'
          : 'px-3 py-2 sm:px-4 [&_button]:h-8 [&_button]:rounded-lg',
        '[&_button]:w-full sm:[&_button]:w-auto',
        className,
      )}
      {...props}
    />
  )
}

const DialogTitle = React.forwardRef<
  React.ComponentRef<typeof DialogPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Title> & { compact?: boolean }
>(({ className, compact = false, ...props }, ref) => (
  <DialogPrimitive.Title
    ref={ref}
    className={cn(
      'font-semibold leading-snug tracking-tight text-foreground',
      compact ? 'text-sm' : 'text-base sm:text-[1.05rem]',
      className,
    )}
    {...props}
  />
))
DialogTitle.displayName = DialogPrimitive.Title.displayName

const DialogDescription = React.forwardRef<
  React.ComponentRef<typeof DialogPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Description> & { compact?: boolean }
>(({ className, compact = false, ...props }, ref) => (
  <DialogPrimitive.Description
    ref={ref}
    className={cn(
      'leading-relaxed text-muted-foreground',
      compact ? 'mt-0.5 text-xs' : 'mt-1 text-[13px] sm:text-sm',
      className,
    )}
    {...props}
  />
))
DialogDescription.displayName = DialogPrimitive.Description.displayName

/** Wrap dialogs that need exit animations */
function DialogAnimate({ open, children }: { open: boolean; children: React.ReactNode }) {
  return <AnimatePresence mode="wait">{open ? children : null}</AnimatePresence>
}

export {
  Dialog,
  DialogPortal,
  DialogOverlay,
  DialogTrigger,
  DialogClose,
  DialogContent,
  DialogHeader,
  DialogBody,
  DialogFooter,
  DialogTitle,
  DialogDescription,
  DialogAnimate,
  dialogContentVariants,
}

export type DialogSize = NonNullable<VariantProps<typeof dialogContentVariants>['size']>
